import React, { useState, useEffect } from "react";
import { Trash, Plus, Minus } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { CountrySelector } from "../components/Selectors/Country";
import { Controller, useForm } from "react-hook-form";
import { editClient } from "../api/clientApi";
import {calculateAramexRate, fetchAramexCities} from "../api/aramexApi";
import i18next from "i18next";

const CART_STORAGE_KEY = "cart";
const COUNTRY_STORAGE_KEY = "selectedCountry";
const EXPIRATION_DAYS = 7;

const getStoredCart = () => {
  try {
    const storedCart = JSON.parse(localStorage.getItem(CART_STORAGE_KEY)) || [];
    const validCart = storedCart.filter(
      (item) =>
        item.addedAt &&
        new Date().getTime() - new Date(item.addedAt).getTime() <
          EXPIRATION_DAYS * 86400000
    );
    return validCart;
  } catch (error) {
    console.error("Error reading cart from localStorage:", error);
    return [];
  }
};

const getStoredCountry = () => {
  try {
    return localStorage.getItem(COUNTRY_STORAGE_KEY);
  } catch (error) {
    console.error("Error reading country from localStorage:", error);
    return null;
  }
};

const setStoredCountry = (country) => {
  try {
    localStorage.setItem(COUNTRY_STORAGE_KEY, country);
  } catch (error) {
    console.error("Error saving country to localStorage:", error);
  }
};

const CartPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [cart, setCart] = useState(getStoredCart);
  const { selectedBranch, setCartTotal, clientData, setClientData } = useAuth();
  const [cities, setCities] = useState([]);
const [selectedCity, setSelectedCity] = useState(null);
const [rateError, setRateError] = useState(null);
const [deliveryReady, setDeliveryReady] = useState(false);

const [localCity, setLocalCity] = useState(clientData?.city || "");
const [localPostal, setLocalPostal] = useState(clientData?.postal_code || "0000");


  const {
    control,
    setValue,
    watch,
  } = useForm();

  const selectedCountry = watch("country");

  // Save selected country to localStorage whenever it changes


  useEffect(() => {
  async function loadCities() {
    if (!selectedCountry) return;

    try {
      const res = await fetchAramexCities(selectedCountry);
      if (res.success) {
        setCities(res.cities || []);
      } else {
        setCities([]);
        console.error("Failed to fetch cities for Aramex");
      }
    } catch (err) {
      setCities([]);
      console.error(err);
    }

    setSelectedCity(null); // reset city when country changes
  }

  loadCities();
     if (selectedCountry) {
      setStoredCountry(selectedCountry);
    }
}, [selectedCountry]);

  useEffect(() => {
    async function setInitialCountry() {
      try {
        // Priority 1: Check localStorage for saved country
        const storedCountry = getStoredCountry();
        if (storedCountry) {
          setValue("country", storedCountry);
          return;
        }

        // Priority 2: Check clientData for country
        if (clientData?.country) {
          setValue("country", clientData.country);
          return;
        }

        // Priority 3: Detect country from IP
        const response = await fetch("https://free.freeipapi.com/api/json");
        const data = await response.json();
        if (data.countryCode) {
          setValue("country", data.countryCode);
        }
      } catch (error) {
        console.error("Failed to set initial country:", error);
        // Fallback to clientData if IP detection fails
        if (clientData?.country) {
          setValue("country", clientData.country);
        }
      }
    }

    setInitialCountry();
  }, [clientData, setValue]);
// ----- 1️⃣ Cart & Delivery Update -----
useEffect(() => {
  async function updateCartAndDelivery() {
    if (!selectedBranch || !selectedCountry || !localCity || !localPostal) {
      setDeliveryReady(false);
      return;
    }

    const totalWeight = cart.reduce(
      (sum, item) => sum + (item.weight || 0.5) * item.quantity,
      0
    );
    const subtotalValue = cart.reduce(
      (sum, item) => sum + item.price * item.quantity,
      0
    );

    const isSameCountry = selectedCountry === selectedBranch.country;
    let internationalRate = null;
    let error = null;

    if (!isSameCountry) {
      try {
        const rateResponse = await calculateAramexRate(
          selectedCountry,
          Math.max(totalWeight, 0.5),
          subtotalValue,
          localCity,
          localPostal
        );

        if (rateResponse.success) {
          internationalRate = rateResponse.amount;
        } else {
          let message = "Failed to calculate shipping.";
          if (rateResponse.message) {
            try {
              const parsed = JSON.parse(rateResponse.message);
              if (Array.isArray(parsed) && parsed[0]?.Message) {
                message = parsed[0].Message;
              } else if (typeof parsed === "string") {
                message = parsed;
              }
            } catch {
              message = rateResponse.message;
            }
          }
          error = message;
        }
      } catch (err) {
        console.error("Failed to fetch Aramex rate:", err);
        error = "Failed to fetch shipping rate.";
      }
    }

    // Update cart items
    const updatedCart = cart.map((item) => {
      const inStock = item.quantity > 0;
      let delivery_time = null;
      let delivery_cost = null;

      if (isSameCountry) {
        delivery_time = inStock
          ? `${selectedBranch.delivery_time} days`
          : `${selectedBranch.delivery_time_out_stock} days`;
        delivery_cost = inStock
          ? selectedBranch.delivery_cost
          : selectedBranch.Inter_delivery_cost;
      } else {
        delivery_time = `${selectedBranch.Inter_delivery_time} days`;
        delivery_cost = internationalRate ?? selectedBranch.Inter_delivery_cost;
      }

      return { ...item, delivery_time, delivery_cost };
    });

    setCart(updatedCart);
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(updatedCart));

    const totalItems = updatedCart.reduce((acc, item) => acc + item.quantity, 0);
    setCartTotal(totalItems);

    // Update error state and deliveryReady
    setRateError(error);
    setDeliveryReady(isSameCountry || (internationalRate !== null && !error));
    // Save shipping address for checkout (guest mode)
if (selectedCountry && localCity && localPostal) {
  localStorage.setItem(
    "shippingAddress",
    JSON.stringify({
      country: selectedCountry,
      city: localCity,
      postal_code: localPostal,
    })
  );
}
  }

  updateCartAndDelivery();
}, [localCity, localPostal, selectedCountry, cart.length, selectedBranch]);


/// ----- 2️⃣ Client Country & Address Update -----
useEffect(() => {
  const timeout = setTimeout(async () => {
    if (!clientData || !selectedCountry) return;
    if (!localCity || !localPostal) return;

    try {
      const processedData = {
        client_id: clientData.client_id,
        country: selectedCountry,
        address: JSON.stringify({
          city: localCity,
          postal_code: localPostal,
        }),
      };

      const client = await editClient(processedData);
      setClientData(client);
    } catch (error) {
      console.error("Failed to update client data:", error);
    }
  }, 500); // wait 500ms after typing stops

  return () => clearTimeout(timeout);
}, [localCity, localPostal, selectedCountry]);// only triggers on changes


  const updateQuantity = (productId, change) => {
    setCart((prevCart) =>
      prevCart.map((item) =>
        item.product_id === productId
          ? { ...item, quantity: Math.max(1, item.quantity + change) }
          : item
      )
    );
  };

  const removeItem = (productId) => {
    setCart((prevCart) =>
      prevCart.filter((item) => item.product_id !== productId)
    );
  };

  const subtotal = cart.reduce(
    (acc, item) => acc + item.price * item.quantity,
    0
  );
  const totalDelivery = cart.reduce(
    (max, item) => Math.max(max, Number(item.delivery_cost || 0)),
    0
  );
  const total = subtotal + totalDelivery;

  const handleCheckout = () => {
    if (totalDelivery == null) {
      toast.error(t("Please select your delivery country to proceed."));
      return;
    }

    localStorage.setItem("cartTotal", total);
    navigate("/checkout", { state: { cart, total, selectedBranch } });
  };

  return (
    <div className="max-w-3xl mx-auto p-6 bg-white shadow-md rounded-lg">
      <h2 className="text-2xl font-bold mb-4">{t("ShoppingCart")}</h2>

      <div className="bg-gray-50 border rounded-lg p-4 mb-6">
        <div className="flex justify-between items-center mb-2">
          <h3 className="font-semibold text-lg text-gray-700">
            {t("Delivery Information")}
          </h3>
          <div className="text-sm text-gray-500 italic">
            {t("Country selection affects delivery charges")}
          </div>
        </div>
        <form id="country-form">
          <Controller
            name="country"
            control={control}
            rules={{ required: t("Country is required") }}
            render={({ field, fieldState: { error } }) => (
              <CountrySelector {...field} error={error?.message} />
            )}
          />
          <div className="mt-4">
  <label className="block text-gray-700 font-medium mb-1">
    {t("City")}
  </label>
<select
  value={localCity}
  onChange={(e) => setLocalCity(e.target.value)}
  className="w-full border p-2 rounded text-black"
  disabled={!cities.length}
>
  <option value="" disabled>{t("Select city")}</option>
  {cities.map((city) => (
    <option key={city} value={city}>{city}</option>
  ))}
</select>

<input
  type="text"
  value={localPostal}
  onChange={(e) => setLocalPostal(e.target.value)}
  className="w-full border p-2 rounded mt-2 text-black"
  placeholder={t("Enter postal code")}
/>
</div>

        </form>
{!deliveryReady && selectedCountry && selectedBranch.country !== selectedCountry && (
  <p className="text-sm text-red-500 mb-2">
    {rateError
      ? rateError
      : t("Please select your city and enter postal code to calculate shipping.")}
  </p>
)}
      </div>

      {cart.length === 0 ? (
        <p className="text-gray-500">{t("cart.empty")}.</p>
      ) : (
        <div>
          {cart.map((item) => (
            <div
              key={item.product_id}
              className="flex items-center justify-between border-b py-4"
            >
              <div className="flex items-center">
                <img
                  src={item.image}
                  alt={item.name}
                  className="w-16 h-16 rounded-lg mr-4"
                />
                <div>
                  <h3 className="text-lg font-semibold">{item.name}</h3>
                  <p className="text-gray-500">
                    {item.price.toFixed(2)} {selectedBranch?.currency}
                  </p>
                  <p className="text-sm text-gray-400 flex gap-1" dir={i18next.language === "ar" ? "rtl" : "ltr"}>
                    {t("DeliveryTime")}: {item.delivery_time || t("Not Available")}
                  </p>
<p className="text-sm text-gray-400 flex gap-1" dir={i18next.language === "ar" ? "rtl" : "ltr"}>
  {t("DeliveryCost")}:
  <span>
    {item.delivery_cost === 0
      ? t("Free Delivery")
      : item.delivery_cost != null
      ? `${item.delivery_cost} ${selectedBranch?.currency}`
      : t("Not Available")}
  </span>
</p>
                </div>
              </div>
              <div className="flex items-center">
                <button
                  onClick={() => updateQuantity(item.product_id, -1)}
                  className="p-2"
                >
                  <Minus size={16} />
                </button>
                <span className="mx-3">{item.quantity}</span>
                <button
                  onClick={() => updateQuantity(item.product_id, 1)}
                  className="p-2"
                >
                  <Plus size={16} />
                </button>
              </div>
              <button
                onClick={() => removeItem(item.product_id)}
                className="text-red-500 p-2"
              >
                <Trash size={20} />
              </button>
            </div>
          ))}

          <div className="mt-6 text-lg">
            <div className="flex justify-between">
              <span>{t("Subtotal")}:</span>
              <span>{subtotal.toFixed(2)}</span>
            </div>
            <div className="flex justify-between items-start">
              <div className="flex items-center space-x-2">
                <span>{t("Delivery")}:</span>
                <div className="relative group cursor-pointer">
                  <svg
                    className="w-4 h-4 text-gray-500 hover:text-gray-700"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path d="M9 7h2v2H9V7zm0 4h2v4H9v-4z" />
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm0 2C4.477 20 0 15.523 0 10S4.477 0 10 0s10 4.477 10 10-4.477 10-10 10z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <div className="absolute top-6 left-1/2 transform -translate-x-1/2 w-64 p-2 bg-white text-sm text-gray-700 border rounded shadow-md opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-10">
                    {t("We only charge the highest delivery cost among items.")}
                  </div>
                </div>
              </div>
              <span dir={i18next.language === "ar" ? "rtl" : "ltr"}>
                    {totalDelivery === 0
    ? t("Free Delivery")
    : totalDelivery > 0
    ? `${totalDelivery.toFixed(2)} ${selectedBranch?.currency}`
    : t("Not Available")}
              </span>
            </div>
            <hr className="my-4" />
            <div className="flex justify-between font-bold text-xl">
              <span>{t("Total")}:</span>
              <span>
                {total.toFixed(2)} {selectedBranch?.currency}
              </span>
            </div>
          </div>

          <button
            onClick={handleCheckout}
            disabled={!deliveryReady}
            className={`mt-6 w-full py-3 rounded-lg font-semibold text-white ${
              totalDelivery != null ? "bg-green-700 hover:bg-green-600" : "bg-gray-400 cursor-not-allowed"
            }`}
          >
            {t("ProceedtoCheckout")} 
          </button>
        </div>
      )}
    </div>
  );
};

export default CartPage;
